import React, { useState } from 'react';
import { useAccount } from 'wagmi';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import PageLayout from '@/components/layout/PageLayout';
import PageHeader from '@/components/layout/PageHeader';
import { Coins, Loader2, Droplet, Zap, ArrowRight } from 'lucide-react';
import { useDemoStore } from '@/store/demoStore';
import toast from 'react-hot-toast';
import { requestFaucetTokens, demoMode } from '@/services/paymentSystem';
import TokenAnimation from '@/components/3d/TokenAnimation';

const Faucet: React.FC = () => {
  const { address, isConnected } = useAccount();
  const { isDemoMode } = useDemoStore();
  const [isLoading, setIsLoading] = useState(false);
  const [txHash, setTxHash] = useState<string | null>(null);

  const requestTokens = async () => {
    if (!address && !isDemoMode) {
      toast.error('Please connect your wallet first');
      return;
    }

    const recipientAddress = isDemoMode 
      ? '0x742d35Cc6634C0532925a3b844Bc454e4438f44e' // Demo address
      : address;

    setIsLoading(true);
    setTxHash(null);

    try {
      if (isDemoMode) {
        // In demo mode, use the demo payment system
        const result = await demoMode.requestFaucetTokens(recipientAddress);
        if (result.status === 'success') {
          setTxHash(result.dusdTxHash);
        }
      } else {
        // First try to use the backend API
        const apiUrl = import.meta.env.VITE_API_URL || 'https://fttf-world-api.bharath-f04.workers.dev';
        
        try {
          const response = await fetch(`${apiUrl}/api/faucet`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ address: recipientAddress }),
          });
          
          const data = await response.json();
          
          if (response.ok && data.success) {
            // Set the transaction hash for display
            setTxHash(data.transactions.fttf);
            toast.success(data.message);
            return;
          }
        } catch (apiError) {
          console.error('API error, falling back to direct method:', apiError);
        }
        
        // If API fails, fall back to direct payment system
        const result = await requestFaucetTokens(recipientAddress);
        
        if (result.status === 'success') {
          setTxHash(result.dusdTxHash);
          toast.success('Successfully received 1000 DUSD and 1000 FTTF tokens!');
        } else if (result.status === 'partial') {
          setTxHash(result.dusdTxHash || result.fttfTxHash);
          toast.warning('Partially received tokens. Please check your wallet.');
        } else {
          toast.error('Failed to send tokens. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error sending tokens:', error);
      toast.error('Failed to send tokens. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageLayout>
      {/* Token Animation Background */}
      <TokenAnimation />
      
      <PageHeader 
        title="Token Faucet" 
        description="Get test tokens to use on the FTTF.World platform"
      />
      
      <div className="max-w-md mx-auto">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="backdrop-blur-sm bg-dark-800/80 border-dark-700">
            <CardHeader>
              <CardTitle className="flex items-center">
                <motion.div
                  animate={{ 
                    rotate: [0, 10, -10, 10, 0],
                    scale: [1, 1.1, 1]
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    repeatDelay: 3
                  }}
                >
                  <Coins className="mr-2 h-6 w-6 text-primary" />
                </motion.div>
                Get Test Tokens
              </CardTitle>
              <CardDescription>
                Request 1000 dUSD and 1000 FTTF tokens to use on the platform
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-dark-700 rounded-lg border border-dark-600">
                  <h3 className="font-medium mb-2 flex items-center">
                    <Droplet className="h-4 w-4 mr-2 text-primary-500" />
                    You will receive:
                  </h3>
                  <ul className="space-y-2">
                    <motion.li 
                      className="flex justify-between p-2 rounded-lg"
                      whileHover={{ backgroundColor: 'rgba(138, 43, 226, 0.1)' }}
                    >
                      <span className="flex items-center">
                        <span className="w-6 h-6 rounded-full bg-gradient-to-br from-primary-500/20 to-secondary-500/20 mr-2 flex items-center justify-center text-xs">
                          $
                        </span>
                        dUSD Tokens:
                      </span>
                      <span className="font-bold text-primary-500">1,000</span>
                    </motion.li>
                    <motion.li 
                      className="flex justify-between p-2 rounded-lg"
                      whileHover={{ backgroundColor: 'rgba(138, 43, 226, 0.1)' }}
                    >
                      <span className="flex items-center">
                        <span className="w-6 h-6 rounded-full bg-gradient-to-br from-primary-500/20 to-secondary-500/20 mr-2 flex items-center justify-center text-xs">
                          F
                        </span>
                        FTTF Tokens:
                      </span>
                      <span className="font-bold text-primary-500">1,000</span>
                    </motion.li>
                  </ul>
                </div>
                
                {txHash && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-green-500/10 text-green-500 rounded-lg border border-green-500/20"
                  >
                    <h3 className="font-medium mb-1 flex items-center">
                      <Zap className="h-4 w-4 mr-2" />
                      Transaction Successful!
                    </h3>
                    <p className="text-xs break-all">
                      TX Hash: {txHash}
                    </p>
                  </motion.div>
                )}
              </div>
            </CardContent>
            <CardFooter>
              <Button 
                className="w-full" 
                onClick={requestTokens} 
                disabled={isLoading || (!isConnected && !isDemoMode)}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending Tokens...
                  </>
                ) : (
                  <>
                    <Droplet className="mr-2 h-4 w-4" />
                    Request Tokens
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="mt-6 text-center text-sm text-muted-foreground"
        >
          <Card className="backdrop-blur-sm bg-dark-800/50 border-dark-700 p-4">
            <p>
              These tokens are for testing purposes only and have no real value.
              {!isConnected && !isDemoMode && (
                <span className="block mt-2 text-primary">
                  Please connect your wallet to request tokens.
                </span>
              )}
            </p>
            
            <div className="mt-4 text-xs text-gray-500">
              <p className="flex items-center justify-center">
                <Zap className="h-3 w-3 mr-1 text-primary-500" />
                Powered by Sonic Blaze Testnet
              </p>
            </div>
          </Card>
        </motion.div>
      </div>
    </PageLayout>
  );
};

export default Faucet;